import { Link, router } from "@inertiajs/react";
import { CheckCheck, Mail, Trash2 } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";

interface Campaign {
  id: number;
  name: string;
  source_type: "job_alert" | "manual_upload" | "skm";
  source_name: string | null;
  source_href: string | null;
  status: string;
  total_count: number;
  cleared_count: number;
  blocked_count: number;
  tentative_count: number;
  sent_count: number;
  ai_drafted: boolean;
  tone: string;
  created_at: string;
}

interface Counts {
  all: number;
  draft_review: number;
  approved: number;
  sent: number;
}

interface Props {
  campaigns: Campaign[];
  tab: string;
  counts: Counts;
}

const SOURCE_CONFIG = {
  job_alert:     { label: "Job Alert",     className: "bg-green-50 text-green-700 border-green-200" },
  manual_upload: { label: "Manual Upload", className: "bg-blue-50 text-blue-700 border-blue-200" },
  skm:           { label: "SKM",           className: "bg-purple-50 text-purple-700 border-purple-200" },
};

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  pending_dnc:  { label: "Pending DNC",   className: "bg-gray-100 text-gray-500 border-gray-200" },
  dnc_complete: { label: "DNC Done",      className: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  drafting:     { label: "Drafting",      className: "bg-blue-50 text-blue-700 border-blue-200" },
  draft_review: { label: "Draft Review",  className: "bg-amber-50 text-amber-700 border-amber-200" },
  approved:     { label: "Approved",      className: "bg-green-50 text-green-700 border-green-200" },
  sending:      { label: "Sending",       className: "bg-blue-50 text-blue-600 border-blue-200" },
  sent:         { label: "Sent",          className: "bg-gray-100 text-gray-500 border-gray-200" },
};

const TABS = [
  { key: "all",          label: "All",          countKey: "all" as const },
  { key: "draft_review", label: "Draft Review", countKey: "draft_review" as const },
  { key: "approved",     label: "Approved",     countKey: "approved" as const },
  { key: "sent",         label: "Sent",         countKey: "sent" as const },
];

function relativeDate(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function deleteCampaign(id: number) {
  if (!confirm("Delete this campaign? This cannot be undone.")) return;
  router.delete(`/email-campaigns/${id}`);
}

function approveCampaign(id: number) {
  router.patch(`/email-campaigns/${id}/approve`);
}

export default function EmailCampaignsIndex({ campaigns, tab, counts }: Props) {
  return (
    <AppLayout title="Email Campaigns">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-800">Email Campaigns</h1>
          <p className="text-sm text-gray-500 mt-0.5">All outreach drafts — job alerts and manual uploads</p>
        </div>
        <Link
          href="/outbound"
          className="no-underline flex items-center gap-1.5 text-sm bg-green-700 text-white px-3 py-1.5 rounded-md hover:bg-green-800 transition-colors"
        >
          <Mail className="w-3.5 h-3.5" /> New Upload
        </Link>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {TABS.map((t) => (
          <Link
            key={t.key}
            href={t.key === "all" ? "/email-campaigns" : `/email-campaigns?tab=${t.key}`}
            className={`no-underline px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? "border-green-600 text-green-700"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {t.label}
            <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
              tab === t.key ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
            }`}>
              {counts[t.countKey]}
            </span>
          </Link>
        ))}
      </div>

      {campaigns.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 py-16 text-center">
          <Mail className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No campaigns yet.</p>
          <Link href="/outbound" className="mt-3 inline-block text-sm text-green-700 hover:underline">
            Upload a list to create your first campaign
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="px-4 py-3 text-left font-medium text-gray-600 text-xs uppercase tracking-wide">Campaign</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 text-xs uppercase tracking-wide">Source</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 text-xs uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 text-xs uppercase tracking-wide">Recipients</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 text-xs uppercase tracking-wide">Created</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {campaigns.map((c) => {
                const src = SOURCE_CONFIG[c.source_type] ?? SOURCE_CONFIG.manual_upload;
                const st  = STATUS_CONFIG[c.status] ?? { label: c.status, className: "bg-gray-100 text-gray-500 border-gray-200" };

                return (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <Link href={`/email-campaigns/${c.id}`} className="font-medium text-gray-900 hover:text-green-700">
                        {c.name}
                      </Link>
                      {c.ai_drafted && (
                        <span className="ml-2 text-xs text-purple-600 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded">AI</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded border ${src.className}`}>
                        {src.label}
                      </span>
                      {c.source_name && c.source_href && (
                        <Link href={c.source_href} className="block text-xs text-gray-400 hover:text-green-700 mt-0.5 truncate max-w-[140px]">
                          {c.source_name}
                        </Link>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded border ${st.className}`}>
                        {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="text-green-700 font-medium">{c.cleared_count} clear</span>
                        {c.blocked_count > 0 && <span className="text-red-500">{c.blocked_count} blocked</span>}
                        {c.tentative_count > 0 && <span className="text-amber-600">{c.tentative_count} tentative</span>}
                        {c.status === "sent" && <span className="text-gray-400">{c.sent_count} sent</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400">{relativeDate(c.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <Link
                          href={`/email-campaigns/${c.id}`}
                          className="text-xs text-green-700 border border-green-200 hover:bg-green-50 rounded px-2 py-1 transition-colors"
                        >
                          Review
                        </Link>
                        {c.status === "draft_review" && (
                          <button
                            onClick={() => approveCampaign(c.id)}
                            className="flex items-center gap-1 text-xs text-gray-600 border border-gray-200 hover:bg-gray-50 rounded px-2 py-1 transition-colors"
                          >
                            <CheckCheck className="w-3 h-3" /> Approve
                          </button>
                        )}
                        <button
                          onClick={() => deleteCampaign(c.id)}
                          className="text-xs text-gray-400 hover:text-red-500 border border-gray-200 hover:border-red-200 rounded px-2 py-1 transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AppLayout>
  );
}
